/**
 * StoryProcessingBanner — one banner for all videos in a story.
 *
 * Shows count-aware copy:
 *  - 1 video processing: "We're listening to your video"
 *  - 1 video pending: "We heard your video" + CTA
 *  - N videos: "We heard your N videos" + ready/pending counts + CTA
 *  - Nothing pending: hidden
 *
 * Props:
 *   videoMediaFileIds: string[]  — IDs of video media files in this story
 *   jobId: string | null        — pending confirmation job ID
 *   onStatusChange: (summary) => void
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRound, CheckCircle2, Ear } from 'lucide-react';
import { getVideoProcessingStatus } from '../../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

export default function StoryProcessingBanner({ videoMediaFileIds, jobId, onStatusChange }) {
  const navigate = useNavigate();
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchStatuses = useCallback(() => {
    if (!videoMediaFileIds?.length) return;
    getVideoProcessingStatus(videoMediaFileIds)
      .then(data => {
        setStatusMap(data || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [videoMediaFileIds]);

  // Poll while anything is processing
  useEffect(() => {
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 8000);
    return () => clearInterval(interval);
  }, [fetchStatuses]);

  // Notify parent of status changes
  useEffect(() => {
    if (loading) return;
    const summary = computeSummary();
    onStatusChange?.(summary);
  }, [statusMap, loading]); // eslint-disable-line

  const computeSummary = () => {
    const total = videoMediaFileIds?.length || 0;
    let processing = 0;
    let pending = 0;
    let ready = 0;

    videoMediaFileIds?.forEach(id => {
      const s = statusMap[id];
      if (!s) { processing++; return; }
      if (s.status === 'processing' || s.status === 'queued' || s.status === 'prescreening' || s.status === 'transcribing') {
        processing++;
      } else if (s.status === 'awaiting_confirmation' || (s.unidentified_speakers > 0)) {
        pending++;
      } else {
        ready++;
      }
    });

    return { total, processing, pending, ready };
  };

  if (loading || !videoMediaFileIds?.length) return null;

  const { total, processing, pending, ready } = computeSummary();

  // Hide when nothing needs attention
  if (processing === 0 && pending === 0) return null;

  // Copy rules
  let title, subtitle, ctaText;
  if (total === 1 && processing > 0) {
    title = "We're listening to your video";
    subtitle = "This takes a few minutes.";
    ctaText = null; // passive while processing
  } else if (total === 1 && pending > 0) {
    title = "We heard your video";
    subtitle = "Just need to know who's talking";
    ctaText = "Tell us who's talking";
  } else {
    title = `We heard your ${total} videos`;
    const parts = [];
    if (ready > 0) parts.push(`${ready} ready to watch`);
    if (pending > 0) parts.push(`${pending} need you`);
    if (processing > 0) parts.push(`${processing} still listening`);
    subtitle = parts.join(' · ');
    ctaText = pending > 0 ? `Answer ${pending} voice question${pending > 1 ? 's' : ''}` : null;
  }

  const handleCTA = () => {
    if (jobId) {
      navigate(`/identify-voices/${jobId}`);
    }
  };

  return (
    <div>
      {/* Banner */}
      <div style={{
        background: '#fff', border: '1px solid #E4E1D2', borderRadius: 14,
        padding: '16px 18px', boxShadow: '0 2px 8px rgba(40,46,28,.05)',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: ctaText ? 12 : 0 }}>
          <div className="processing-banner-pulse" style={{
            width: 38, height: 38, borderRadius: '50%', background: '#FFF3E6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <UserRound size={20} color="#E65100" />
          </div>
          <div>
            <p style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 16, color: '#2C2F26', margin: 0 }}>
              {title}
            </p>
            <p style={{ fontSize: 14, color: '#5A6150', margin: '2px 0 0', fontFamily: FONT_B }}>
              {subtitle}
            </p>
          </div>
        </div>
        {ctaText && jobId && (
          <button onClick={handleCTA} style={{
            width: '100%', background: '#E65100', color: '#fff', border: 'none',
            borderRadius: 11, padding: '12px', fontSize: 15, fontWeight: 700,
            cursor: 'pointer', fontFamily: FONT_B, minHeight: 44,
          }}>
            {ctaText}
          </button>
        )}
      </div>

      {/* Thumbnail grid (only for >1 video) */}
      {total > 1 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap: 8, marginBottom: 16,
        }}>
          {videoMediaFileIds.map(id => {
            const s = statusMap[id];
            const isProcessing = !s || ['processing', 'queued', 'prescreening', 'transcribing'].includes(s?.status);
            const needsYou = s?.status === 'awaiting_confirmation' || (s?.unidentified_speakers > 0);
            const isReady = !isProcessing && !needsYou;
            const thumbUrl = s?.thumbnail_url;

            return (
              <div key={id} style={{
                aspectRatio: '1', borderRadius: 10, overflow: 'hidden',
                position: 'relative', background: '#EFEEE5',
              }}>
                {thumbUrl && (
                  <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                )}
                {/* Listening overlay */}
                {isProcessing && (
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Ear size={22} color="#fff" className="processing-banner-pulse" />
                  </div>
                )}
                {/* Status pill */}
                <div style={{
                  position: 'absolute', bottom: 4, left: 4,
                  borderRadius: 999, padding: '2px 7px', fontSize: 10, fontWeight: 700,
                  fontFamily: FONT_B, display: 'flex', alignItems: 'center', gap: 3,
                  ...(isReady ? { background: '#fff', color: '#556B2F' } :
                    needsYou ? { background: '#FFF3E6', border: '1px solid #F0D9B4', color: '#C65D00' } :
                    { background: 'rgba(255,255,255,.85)', color: '#6E7562' }),
                }}>
                  {isReady && <><CheckCircle2 size={10} /> Ready</>}
                  {needsYou && <><UserRound size={10} /> Needs you</>}
                  {isProcessing && 'Listening…'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .processing-banner-pulse { animation: bannerPulse 1.6s ease-in-out infinite; }
        @keyframes bannerPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @media (prefers-reduced-motion: reduce) {
          .processing-banner-pulse { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
